import React from "react";

import { Outlet, useLocation } from "react-router-dom";

import Stack from "@mui/material/Stack";

import FreeTrialEndedAlert from "@app/analytics/account/Billing/components/FreeTrialEndedAlert";
import FreeTrialModal from "@app/analytics/account/Billing/components/FreeTrialModal";
import CalendarBanner from "@app/analytics/components/CalendarBanner";
import DailyReadPlayerDialog from "@app/analytics/components/DailyReadPlayerDialog";
import FirstTimeSettingsBanner from "@app/analytics/components/FirstTimeSettingsBanner";
import InvitePendingAlert from "@app/analytics/components/InvitePendingAlert";
import ReauthorizeProviderAlerts from "@app/analytics/components/ReauthorizeProviderAlerts";
import RssUpsellBanner from "@app/analytics/components/RssUpsellBanner";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import UploadModal from "@app/analytics/upload/UploadModal";
import useAuth from "@app/common/auth/useAuth";
import Toasts from "@app/common/containers/Toasts";
import useSiteStatusBanner from "@app/common/hooks/useSiteStatusBanner";
import useViewport from "@app/common/hooks/useViewport";
import useWindowDimensions from "@app/common/hooks/useWindowDimensions";
import ROUTES from "@app/common/routes";
import { UserWorkspace } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import Config from "@app/config";

import SiteStatusBanner from "@rui/SiteStatusBanner";
import UpdatedDataProcessingAgreementDialog from "@rui/UpdatedDataProcessingAgreementDialog";
import UpdatedPolicyBanner from "@rui/UpdatedPolicyBanner";

import MobileNav from "../MobileNav";
import { OFFSET_NAV_SPACING } from "../MobileNav/styles";
import RollupFilter from "../RollupFilter";
import RollupFilterFree from "../RollupFilter/RollupFilterFree";
import SideNav from "../SideNav";

const ROLLUP_ROUTES = [
  ROUTES.MEETING_REPORTS,
  ROUTES.MEETING_REPORTS_SEARCH,
  ROUTES.SHARED_MEETING_REPORTS,
  ROUTES.INCOMPLETE_MEETING_REPORTS,
  ROUTES.SLUGS.MEETING_POLICY,
];

const isRollupRoute = (
  currentPath: Location["pathname"],
  workspaceId?: UserWorkspace["id"]
): boolean => {
  if (ROLLUP_ROUTES.includes(currentPath)) {
    return true;
  }

  if (workspaceId && currentPath.includes(workspaceId)) {
    const slug = currentPath.split("/").pop() ?? "";
    return ROLLUP_ROUTES.includes(slug);
  }

  return false;
};

const SideNavLayout = () => {
  const { isMobileView } = useWindowDimensions();
  const location = useLocation();

  const { user } = useAuth();
  const { currentPlan, plansInitialLoading } = useBillingContext();
  const { inWorkspace, workspaceId } = useWorkspace();

  const { isEnabled, severity, statusText, statusLoading } =
    useSiteStatusBanner("analytics");

  const showMobileNav = Config.flags.enableMobileNav && isMobileView;
  // Forces a min width and the page to be zoomed out to fit content
  useViewport(
    showMobileNav
      ? { width: 500, initialScale: 0.7 }
      : { width: 1400, initialScale: 0.1 }
  );

  const showRollupFilter =
    inWorkspace &&
    isRollupRoute(location.pathname, workspaceId) &&
    !plansInitialLoading;

  // don't show banners in the checkout flow. looks weird
  const shouldShowBanners = !location.pathname.includes(
    ROUTES.BILLING_CHECKOUT_ROOT
  );

  return (
    <>
      <FreeTrialModal />
      <UpdatedDataProcessingAgreementDialog />
      <Stack direction="row" justifyContent="stretch" flexGrow={1}>
        {user && showMobileNav ? <MobileNav /> : <SideNav />}
        <Stack
          sx={{
            position: "relative",
            flexGrow: 1,
            minWidth: showMobileNav ? "100%" : "700px",
            marginTop: showMobileNav ? OFFSET_NAV_SPACING : null,
          }}
        >
          <Toasts />
          <Stack sx={{ display: shouldShowBanners ? undefined : "none" }}>
            {isEnabled && !statusLoading && (
              <SiteStatusBanner severity={severity} text={statusText} />
            )}
            <UpdatedPolicyBanner />
            <FirstTimeSettingsBanner priority={1} />
            <ReauthorizeProviderAlerts priority={2} />
            <InvitePendingAlert priority={3} />
            <FreeTrialEndedAlert priority={5} />
            <RssUpsellBanner priority={100} />
            <CalendarBanner />

            {showRollupFilter &&
              (currentPlan.type === "free" ? (
                <RollupFilterFree />
              ) : (
                <RollupFilter />
              ))}
          </Stack>
          <Stack sx={{ flexGrow: 1 }}>
            <Outlet />
          </Stack>
        </Stack>
      </Stack>
      {Boolean(user) && (
        <>
          <UploadModal />
          <DailyReadPlayerDialog />
        </>
      )}
    </>
  );
};

export default SideNavLayout;
